import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import logoH from "@/assets/logo_h_3d.png";
import { Spinner, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  const [inviteInfo, setInviteInfo] = useState(null);
  const [inviteError, setInviteError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", password: "", confirm: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setInviteError("Invalid invite link.");
      setLoading(false);
      return;
    }
    fetch(`${API}/team/accept-invite?token=${token}`)
      .then(r => r.ok ? r.json() : r.json().then(d => Promise.reject(d)))
      .then(data => { setInviteInfo(data); setLoading(false); })
      .catch(e => { setInviteError(e?.detail || "Invalid or expired invite link."); setLoading(false); });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Enter your name"); return; }
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (form.password !== form.confirm) { toast.error("Passwords don't match"); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/team/accept-invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, name: form.name.trim(), password: form.password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Failed to activate account");
      localStorage.setItem("session_token", data.session_token);
      await checkAuth();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const roleLabel = { co_host: "Co-host", cleaner: "Cleaner" }[inviteInfo?.role] || inviteInfo?.role;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl shadow-lg border border-slate-100 dark:border-slate-800 overflow-hidden">

        <div className="px-8 pt-8 pb-6 border-b border-slate-100 dark:border-slate-800 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src={logoH} alt="HostFolio" className="w-10 h-10 object-contain" />
            <h1 className="font-manrope text-2xl font-bold">
              <span className="text-slate-800 dark:text-white">Host</span>
              <span className="text-amber-500">Folio</span>
            </h1>
          </div>
          <h2 className="text-lg font-semibold text-slate-800 dark:text-white">You've been invited</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Set up your account to get started</p>
        </div>

        <div className="p-8">
          {loading && (
            <div className="flex justify-center py-8">
              <Spinner size={24} className="animate-spin text-amber-500" />
            </div>
          )}

          {!loading && inviteError && (
            <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-400 text-sm">
              <Warning size={18} weight="fill" className="shrink-0" />
              {inviteError}
            </div>
          )}

          {!loading && inviteInfo && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl">
                <CheckCircle size={18} weight="fill" className="text-amber-500 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-300">{inviteInfo.email || inviteInfo.phone}</p>
                  <p className="text-xs text-amber-600 dark:text-amber-400">{roleLabel} access</p>
                  {(inviteInfo.email || inviteInfo.phone) && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">Use this {inviteInfo.email ? "email" : "phone number"} and your password to sign in next time.</p>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-slate-600 dark:text-slate-300 text-xs font-semibold">Your Name</Label>
                <Input
                  className="mt-1 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-600 text-slate-800 dark:text-white focus:border-amber-400"
                  placeholder="Full name"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  autoFocus
                />
              </div>

              <div>
                <Label className="text-slate-600 dark:text-slate-300 text-xs font-semibold">Password</Label>
                <Input
                  type="password"
                  className="mt-1 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-600 text-slate-800 dark:text-white focus:border-amber-400"
                  placeholder="At least 8 characters"
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                />
              </div>

              <div>
                <Label className="text-slate-600 dark:text-slate-300 text-xs font-semibold">Confirm Password</Label>
                <Input
                  type="password"
                  className="mt-1 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-600 text-slate-800 dark:text-white focus:border-amber-400"
                  placeholder="Repeat password"
                  value={form.confirm}
                  onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-amber-400 hover:bg-amber-500 disabled:opacity-50 text-white font-semibold rounded-xl py-3 text-sm transition flex items-center justify-center gap-2"
              >
                {submitting
                  ? <><Spinner size={15} className="animate-spin" /> Setting up…</>
                  : "Set Up Account"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
